import { useState } from 'react';
import { FaLock } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { changeMyPassword } from '../../services/superAdminApi';

// Forced first-login password change for newly-added superadmin team members.
// Rendered by SuperAdminLayout in place of the panel while mustChangePassword is true.
export default function ForcePasswordChange({ superAdmin, onDone, onSignOut }) {
  const [current, setCurrent] = useState('');
  const [next,    setNext]    = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy,    setBusy]    = useState(false);
  const [error,   setError]   = useState('');

  const checks = [
    { ok: next.length >= 8,       label: '8+ characters' },
    { ok: /[a-zA-Z]/.test(next),  label: 'A letter' },
    { ok: /\d/.test(next),        label: 'A number' },
    { ok: next.length > 0 && next === confirm, label: 'Passwords match' },
  ];
  const nextValid = next.length >= 8 && /[a-zA-Z]/.test(next) && /\d/.test(next);
  const canSubmit = !busy && current && nextValid && next === confirm;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError('');
    try {
      await changeMyPassword({ currentPassword: current, newPassword: next });
      toast.success('Password updated');
      onDone(); // clears the flag in context → falls through to the panel
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    } finally {
      setBusy(false);
    }
  };

  const inputStyle = {
    width: '100%', background: '#1a1918', border: '1px solid rgba(237,232,224,0.08)',
    borderRadius: 12, padding: '11px 12px', color: '#ede8e0', fontSize: 13, outline: 'none', colorScheme: 'dark',
  };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: '#8a847c', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: '#141312' }}>
      <div className="w-full max-w-sm">
        <div className="flex flex-col items-center mb-8">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-4" style={{ background: '#c8102e' }}>
            <FaLock className="text-white text-sm" />
          </div>
          <h1 className="text-[#ede8e0] font-semibold text-lg">Set your new password</h1>
          <p className="text-xs mt-1 text-center" style={{ color: '#8a847c' }}>
            Welcome{superAdmin?.name ? `, ${superAdmin.name}` : ''}. Choose a new password before continuing.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label style={labelStyle}>Temporary Password</label>
            <input style={inputStyle} type="password" value={current} autoComplete="current-password"
              onChange={e => setCurrent(e.target.value)} placeholder="The password you were given" />
          </div>
          <div>
            <label style={labelStyle}>New Password</label>
            <input style={inputStyle} type="password" value={next} autoComplete="new-password"
              onChange={e => setNext(e.target.value)} placeholder="New password" />
          </div>
          <div>
            <label style={labelStyle}>Confirm New Password</label>
            <input style={{ ...inputStyle, borderColor: confirm && confirm !== next ? '#ff453a' : 'rgba(237,232,224,0.08)' }}
              type="password" value={confirm} autoComplete="new-password"
              onChange={e => setConfirm(e.target.value)} placeholder="Re-enter new password" />
          </div>

          {(next || confirm) && (
            <div className="flex flex-wrap gap-x-3 gap-y-1 pt-0.5">
              {checks.map(c => (
                <span key={c.label} className="text-xs" style={{ color: c.ok ? '#34d399' : '#6b6560' }}>
                  {c.ok ? '✓' : '○'} {c.label}
                </span>
              ))}
            </div>
          )}

          {error && <p className="text-xs" style={{ color: '#ff453a' }}>{error}</p>}

          <button type="submit" disabled={!canSubmit}
            className="w-full py-3 rounded-full font-semibold text-sm transition-all duration-200 mt-1 text-white disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: '#c8102e' }}>
            {busy ? 'Saving…' : 'Set Password & Continue'}
          </button>
        </form>

        <button type="button" onClick={onSignOut}
          className="mt-6 w-full text-center text-xs uppercase tracking-widest font-medium transition-colors"
          style={{ color: '#6b6560' }}
          onMouseEnter={e => (e.currentTarget.style.color = '#8a847c')}
          onMouseLeave={e => (e.currentTarget.style.color = '#6b6560')}>
          Sign out
        </button>
      </div>
    </div>
  );
}
